/**
 * PackplatzTimer — Admin-Seite für Packplatz-Timer Monitoring.
 *
 * 3 Tabs:
 *   1. Live: KPI-Bar (4 Cards) + Stations-Grid mit Live-Polling (5s)
 *   2. Tagesauswertung: Datum-Picker + MA-Ranking + Effizienz-Chart
 *   3. Sessions: Filterbar + paginierte Tabelle + CSV-Export
 *
 * @page /packplatz-timer
 * @roles administrator, ops_manager
 * @endpoints GET /api/pack-timer/overview, /stats, /sessions, /stationen
 * @since v2.342.0
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Timer, Package, TrendingUp, Users, Clock, ChevronRight, Download, RefreshCw, X, AlertTriangle, CheckCircle2, ArrowUpRight, ArrowDownRight, Eye, ExternalLink, Filter } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from 'recharts';
import { useLanguage } from '../context/LanguageContext';

const API = import.meta.env.VITE_API_URL || '';
const f = (url, opts) => fetch(`${API}${url}`, { credentials: 'include', ...opts });

export default function PackplatzTimer() {
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'live');

  const tabs = [
    { key: 'live', label: t('packTimer.tab.live', 'Live'), Icon: Timer },
    { key: 'auswertung', label: t('packTimer.tab.auswertung', 'Tagesauswertung'), Icon: TrendingUp },
    { key: 'sessions', label: t('packTimer.tab.sessions', 'Sessions'), Icon: Package },
  ];

  const switchTab = (key) => {
    setActiveTab(key);
    setSearchParams({ tab: key });
  };

  return (
    <div className="p-page space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[var(--text-primary)]">{t('packTimer.title', 'Packplatz-Timer')}</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-0.5">{t('packTimer.subtitle', 'Live-Monitoring aller Packstationen')}</p>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="inline-flex bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-line">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => switchTab(tab.key)}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-[8px] text-sm font-medium transition-all ${
              activeTab === tab.key
                ? 'bg-white shadow-sm text-[var(--text-primary)] dark:bg-surface-elevated'
                : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
            }`}
          >
            <tab.Icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'live' && <LiveTab t={t} />}
      {activeTab === 'auswertung' && <AuswertungTab t={t} />}
      {activeTab === 'sessions' && <SessionsTab t={t} />}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
//  TAB 1: LIVE — KPI + Station Grid
// ═══════════════════════════════════════════════════════════════

function LiveTab({ t }) {
  const [stats, setStats] = useState(null);
  const [stationen, setStationen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStation, setSelectedStation] = useState(null);
  const intervalRef = useRef(null);

  const loadData = useCallback(async () => {
    try {
      const [statsRes, overviewRes] = await Promise.all([
        f('/api/pack-timer/stats').then(r => r.json()),
        f('/api/pack-timer/overview').then(r => r.json()),
      ]);
      setStats(statsRes);
      setStationen(overviewRes.stationen || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
    intervalRef.current = setInterval(loadData, 5000);
    return () => clearInterval(intervalRef.current);
  }, [loadData]);

  if (loading) return <div className="py-12 text-center text-sm text-[var(--text-tertiary)]">{t('common.loading', 'Laden...')}</div>;

  const kpis = [
    { label: t('packTimer.kpi.auftraege', 'Auftr\u00e4ge heute'), value: stats?.auftraege_gesamt ?? 0, icon: Package },
    { label: t('packTimer.kpi.aktiveStationen', 'Aktive Stationen'), value: stats?.aktuell_laufend ?? 0, icon: Timer },
    { label: t('packTimer.kpi.avgEffizienz', '\u00d8 Effizienz'), value: `${stats?.avg_effizienz ?? 0}%`, icon: TrendingUp, highlight: true },
    { label: t('packTimer.kpi.avgZeit', '\u00d8 Zeit/Auftrag'), value: `${stats?.avg_zeit_pro_auftrag ?? 0} min`, icon: Clock },
  ];

  return (
    <div className="space-y-5">
      {/* KPI Bar */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((kpi, i) => (
          <div key={i} className="bg-surface-elevated rounded-card border border-line p-4">
            <div className="flex items-center gap-2 mb-2">
              <kpi.icon className="w-4 h-4 text-[var(--text-tertiary)]" />
              <span className="text-xs text-[var(--text-tertiary)]">{kpi.label}</span>
            </div>
            <div className="text-xl font-bold text-[var(--text-primary)]">{kpi.value}</div>
            {kpi.highlight && stats?.in_time != null && (
              <div className="flex items-center gap-2 mt-1 text-xs text-[var(--text-secondary)]">
                <span className="text-green-600">{stats.in_time} {t('packTimer.inTime', 'in time')}</span>
                <span className="text-[var(--text-tertiary)]">/</span>
                <span className="text-red-500">{stats.over_time} {t('packTimer.overTime', '\u00fcber SOLL')}</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Station Grid */}
      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
        {stationen.map(st => (
          <StationCard key={st.id} station={st} t={t} onClick={() => setSelectedStation(st)} />
        ))}
        {stationen.length === 0 && (
          <div className="col-span-full py-12 text-center text-sm text-[var(--text-tertiary)]">
            {t('packTimer.noStations', 'Keine aktiven Stationen')}
          </div>
        )}
      </div>

      {/* Slide-Over */}
      {selectedStation && (
        <StationSlideOver station={selectedStation} t={t} onClose={() => setSelectedStation(null)} />
      )}
    </div>
  );
}

function StationCard({ station, t, onClick }) {
  const isRunning = !!station.session;
  const elapsed = station.session ? station.session.elapsed_minuten : 0;
  const soll = station.session ? station.session.soll_minuten : 0;
  const pctUsed = soll > 0 ? Math.min(150, (elapsed / soll) * 100) : 0;

  let statusColor = 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';
  let ringColor = '#E5E7EB';
  if (isRunning) {
    if (pctUsed < 50) { statusColor = 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400'; ringColor = '#22C55E'; }
    else if (pctUsed < 100) { statusColor = 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'; ringColor = '#EAB308'; }
    else { statusColor = 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400'; ringColor = '#EF4444'; }
  }

  return (
    <div
      onClick={onClick}
      className="bg-surface-elevated rounded-card border border-line p-4 cursor-pointer hover:border-[#338edc]/40 transition-colors"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${isRunning ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`} />
          <span className="text-sm font-semibold text-[var(--text-primary)]">{station.name}</span>
        </div>
        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${statusColor}`}>
          {isRunning ? t('packTimer.status.running', 'L\u00e4uft') : t('packTimer.status.idle', 'Frei')}
        </span>
      </div>

      {station.ma_name && (
        <div className="text-xs text-[var(--text-secondary)] mb-2 flex items-center gap-1">
          <Users className="w-3 h-3" /> {station.ma_name}
        </div>
      )}

      {isRunning && station.session && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="text-[var(--text-secondary)]">{station.session.kunde_name}</span>
            <span className="text-[var(--text-primary)] font-mono font-medium">
              {elapsed.toFixed(1)} / {soll.toFixed(1)} min
            </span>
          </div>
          {/* Progress bar */}
          <div className="h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${Math.min(100, pctUsed)}%`, backgroundColor: ringColor }}
            />
          </div>
          <div className="flex items-center justify-between text-[10px] text-[var(--text-tertiary)]">
            <span>{station.session.artikel_count} {t('packTimer.artikel', 'Artikel')}</span>
            {station.session.is_sop && <span className="bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded dark:bg-blue-900/30 dark:text-blue-400">SOP</span>}
          </div>
        </div>
      )}

      {!isRunning && (
        <div className="text-xs text-[var(--text-tertiary)] mt-1">{t('packTimer.wartetAufScan', 'Wartet auf Scan...')}</div>
      )}
    </div>
  );
}

function StationSlideOver({ station, t, onClose }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    f(`/api/pack-timer/sessions?station_id=${station.id}&datum=${today}&limit=20`)
      .then(r => r.json())
      .then(d => setSessions(d.sessions || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [station.id]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />
      <div className="relative w-full max-w-md bg-surface-elevated shadow-xl overflow-y-auto">
        <div className="sticky top-0 bg-surface-elevated border-b border-line px-5 py-4 flex items-center justify-between z-10">
          <div>
            <h3 className="text-base font-semibold text-[var(--text-primary)]">{station.name}</h3>
            <p className="text-xs text-[var(--text-secondary)]">{station.ma_name || t('packTimer.keinMA', 'Kein MA zugewiesen')}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-surface-hover text-[var(--text-tertiary)]">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Current session */}
        {station.session && (
          <div className="px-5 py-4 border-b border-line">
            <div className="text-xs font-medium text-[var(--text-tertiary)] uppercase mb-2">{t('packTimer.aktuellerAuftrag', 'Aktueller Auftrag')}</div>
            <div className="bg-surface rounded-lg border border-line p-3 space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-[var(--text-secondary)]">{t('packTimer.kunde', 'Kunde')}</span>
                <span className="font-medium text-[var(--text-primary)]">{station.session.kunde_name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[var(--text-secondary)]">{t('packTimer.artikel', 'Artikel')}</span>
                <span className="font-medium text-[var(--text-primary)]">{station.session.artikel_count}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[var(--text-secondary)]">SOLL</span>
                <span className="font-medium text-[var(--text-primary)]">{station.session.soll_minuten.toFixed(1)} min</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[var(--text-secondary)]">{t('packTimer.elapsed', 'Vergangen')}</span>
                <span className="font-mono font-medium text-[var(--text-primary)]">{station.session.elapsed_minuten.toFixed(1)} min</span>
              </div>
            </div>
          </div>
        )}

        {/* Today's sessions */}
        <div className="px-5 py-4">
          <div className="text-xs font-medium text-[var(--text-tertiary)] uppercase mb-3">{t('packTimer.heuteAbgeschlossen', 'Heute abgeschlossen')}</div>
          {loading ? (
            <div className="py-8 text-center text-sm text-[var(--text-tertiary)]">{t('common.loading', 'Laden...')}</div>
          ) : sessions.filter(s => s.status === 'completed').length === 0 ? (
            <div className="py-8 text-center text-sm text-[var(--text-tertiary)]">{t('packTimer.keineSessionsHeute', 'Keine Sessions heute')}</div>
          ) : (
            <div className="space-y-2">
              {sessions.filter(s => s.status === 'completed').map(s => (
                <div key={s.id} className="flex items-center justify-between text-sm py-2 border-b border-line last:border-0">
                  <div>
                    <div className="text-[var(--text-primary)]">{s.kunde_name}</div>
                    <div className="text-[10px] text-[var(--text-tertiary)]">{s.artikel_count} Art. | {parseFloat(s.soll_minuten).toFixed(1)} → {parseFloat(s.ist_minuten).toFixed(1)} min</div>
                  </div>
                  <EffizienzBadge pct={parseFloat(s.effizienz_pct)} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Display link */}
        <div className="px-5 py-4 border-t border-line">
          <a
            href="/packstation"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm text-[#338edc] hover:underline"
          >
            <ExternalLink className="w-4 h-4" />
            {t('packTimer.displayOeffnen', 'Display \u00f6ffnen')}
          </a>
        </div>
      </div>
    </div>
  );
}

function EffizienzBadge({ pct }) {
  if (pct == null) return null;
  const color = pct >= 100 ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                pct >= 80 ? 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400';
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${color}`}>
      {pct.toFixed(0)}%
    </span>
  );
}


// ═══════════════════════════════════════════════════════════════
//  TAB 2: TAGESAUSWERTUNG — Date Picker + Ranking + Chart
// ═══════════════════════════════════════════════════════════════

function AuswertungTab({ t }) {
  const [datum, setDatum] = useState(() => new Date().toISOString().slice(0, 10));
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, sessRes] = await Promise.all([
        f(`/api/pack-timer/stats?datum=${datum}`).then(r => r.json()),
        f(`/api/pack-timer/sessions?datum=${datum}&limit=500`).then(r => r.json()),
      ]);
      setStats(statsRes);
      setSessions(sessRes.sessions || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [datum]);

  useEffect(() => { loadData(); }, [loadData]);

  // MA ranking
  const maMap = {};
  for (const s of sessions.filter(s => s.status === 'completed')) {
    const name = s.ma_name || 'Unbekannt';
    if (!maMap[name]) maMap[name] = { name, count: 0, totalEff: 0, totalIst: 0, totalSoll: 0 };
    maMap[name].count++;
    maMap[name].totalEff += parseFloat(s.effizienz_pct) || 0;
    maMap[name].totalIst += parseFloat(s.ist_minuten) || 0;
    maMap[name].totalSoll += parseFloat(s.soll_minuten) || 0;
  }
  const ranking = Object.values(maMap)
    .map(m => ({ ...m, avgEff: m.count > 0 ? m.totalEff / m.count : 0 }))
    .sort((a, b) => b.avgEff - a.avgEff);

  // Hourly chart
  const hourMap = {};
  for (const s of sessions.filter(s => s.status === 'completed' && s.started_at)) {
    const h = new Date(s.started_at).getHours();
    if (!hourMap[h]) hourMap[h] = { hour: h, count: 0, totalEff: 0 };
    hourMap[h].count++;
    hourMap[h].totalEff += parseFloat(s.effizienz_pct) || 0;
  }
  const chartData = [];
  for (let h = 6; h <= 20; h++) {
    const entry = hourMap[h];
    chartData.push({ hour: `${h}:00`, avgEff: entry ? Math.round(entry.totalEff / entry.count) : 0, count: entry?.count || 0 });
  }

  if (loading) return <div className="py-12 text-center text-sm text-[var(--text-tertiary)]">{t('common.loading', 'Laden...')}</div>;

  return (
    <div className="space-y-5">
      {/* Date picker + KPIs */}
      <div className="flex items-center gap-4">
        <input
          type="date"
          value={datum}
          onChange={(e) => setDatum(e.target.value)}
          className="px-3 py-1.5 text-sm rounded-lg border border-line bg-surface-elevated text-content focus:outline-none focus:ring-2 focus:ring-primary-500/30"
        />
        <div className="flex items-center gap-4 text-sm text-[var(--text-secondary)]">
          <span><strong className="text-[var(--text-primary)]">{stats?.auftraege_gesamt || 0}</strong> {t('packTimer.auftraege', 'Auftr\u00e4ge')}</span>
          <span><strong className="text-[var(--text-primary)]">{stats?.avg_effizienz || 0}%</strong> {t('packTimer.kpi.avgEffizienz', '\u00d8 Effizienz')}</span>
          <span className="text-green-600">{stats?.in_time || 0} {t('packTimer.inTime', 'in time')}</span>
          <span className="text-red-500">{stats?.over_time || 0} {t('packTimer.overTime', '\u00fcber SOLL')}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        {/* MA Ranking */}
        <div className="bg-surface-elevated rounded-card border border-line">
          <div className="px-5 py-3 border-b border-line">
            <h3 className="text-sm font-semibold text-[var(--text-primary)]">{t('packTimer.maRanking', 'MA-Ranking')}</h3>
          </div>
          <div className="p-5">
            {ranking.length === 0 ? (
              <div className="py-8 text-center text-sm text-[var(--text-tertiary)]">{t('packTimer.keineDaten', 'Keine Daten')}</div>
            ) : (
              <div className="space-y-2">
                {ranking.map((ma, i) => (
                  <div key={ma.name} className="flex items-center gap-3 py-2 border-b border-line last:border-0">
                    <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${
                      i === 0 ? 'bg-yellow-100 text-yellow-700' : i === 1 ? 'bg-gray-100 text-gray-600' : i === 2 ? 'bg-orange-100 text-orange-700' : 'bg-surface text-[var(--text-tertiary)]'
                    }`}>{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[var(--text-primary)] truncate">{ma.name}</div>
                      <div className="text-[10px] text-[var(--text-tertiary)]">{ma.count} {t('packTimer.auftraege', 'Auftr\u00e4ge')} | {ma.totalIst.toFixed(1)} min</div>
                    </div>
                    <EffizienzBadge pct={ma.avgEff} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Hourly Chart */}
        <div className="bg-surface-elevated rounded-card border border-line">
          <div className="px-5 py-3 border-b border-line">
            <h3 className="text-sm font-semibold text-[var(--text-primary)]">{t('packTimer.effizienzProStunde', 'Effizienz pro Stunde')}</h3>
          </div>
          <div className="p-5">
            {sessions.length === 0 ? (
              <div className="py-8 text-center text-sm text-[var(--text-tertiary)]">{t('packTimer.keineDaten', 'Keine Daten')}</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-line, #E5E7EB)" />
                  <XAxis dataKey="hour" tick={{ fontSize: 11, fill: 'var(--text-tertiary, #6B7280)' }} />
                  <YAxis tick={{ fontSize: 11, fill: 'var(--text-tertiary, #6B7280)' }} domain={[0, 150]} />
                  <Tooltip
                    contentStyle={{ fontSize: 12, background: 'var(--bg-surface-elevated, #fff)', border: '1px solid var(--color-line, #E5E7EB)', borderRadius: 8 }}
                    formatter={(value, name) => [`${value}%`, 'Effizienz']}
                    labelFormatter={(l) => `${l} Uhr`}
                  />
                  <ReferenceLine y={100} stroke="#338edc" strokeDasharray="4 4" strokeWidth={1.5} />
                  <Bar dataKey="avgEff" radius={[4, 4, 0, 0]} maxBarSize={32}>
                    {chartData.map((entry, i) => (
                      <Cell key={i} fill={entry.avgEff >= 100 ? '#338edc' : entry.avgEff >= 80 ? '#22D3EE' : '#E5E7EB'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
//  TAB 3: SESSIONS — Filters + Paginated Table + CSV
// ═══════════════════════════════════════════════════════════════

function SessionsTab({ t }) {
  const [sessions, setSessions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ datum: new Date().toISOString().slice(0, 10), station_id: '', ma_id: '', status: '' });
  const [stationen, setStationen] = useState([]);
  const limit = 50;

  useEffect(() => {
    f('/api/pack-timer/stationen').then(r => r.json()).then(d => setStationen(d.stationen || [])).catch(() => {});
  }, []);

  const loadSessions = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.datum) params.set('datum', filters.datum);
    if (filters.station_id) params.set('station_id', filters.station_id);
    if (filters.ma_id) params.set('ma_id', filters.ma_id);
    if (filters.status) params.set('status', filters.status);
    params.set('page', page);
    params.set('limit', limit);

    try {
      const res = await f(`/api/pack-timer/sessions?${params}`).then(r => r.json());
      setSessions(res.sessions || []);
      setTotal(res.total || 0);
    } catch { /* ignore */ }
    setLoading(false);
  }, [filters, page]);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  const exportCsv = () => {
    const headers = ['Datum', 'Station', 'Mitarbeiter', 'Kunde', 'Artikel', 'SOLL (min)', 'IST (min)', 'Effizienz (%)', 'Status'];
    const rows = sessions.map(s => [
      s.started_at ? new Date(s.started_at).toLocaleString('de-AT') : '',
      s.station_name || '',
      s.ma_name || '',
      s.kunde_name || '',
      s.artikel_count,
      s.soll_minuten?.toFixed(2) || '',
      s.ist_minuten?.toFixed(2) || '',
      s.effizienz_pct?.toFixed(1) || '',
      s.status,
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(';')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pack-timer-sessions-${filters.datum || 'all'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="text-[10px] text-[var(--text-tertiary)] uppercase">{t('packTimer.datum', 'Datum')}</label>
          <input
            type="date"
            value={filters.datum}
            onChange={(e) => { setFilters(f => ({ ...f, datum: e.target.value })); setPage(1); }}
            className="block px-3 py-1.5 text-sm rounded-lg border border-line bg-surface-elevated text-content focus:outline-none focus:ring-2 focus:ring-primary-500/30"
          />
        </div>
        <div>
          <label className="text-[10px] text-[var(--text-tertiary)] uppercase">{t('packTimer.station', 'Station')}</label>
          <select
            value={filters.station_id}
            onChange={(e) => { setFilters(f => ({ ...f, station_id: e.target.value })); setPage(1); }}
            className="block px-3 py-1.5 text-sm rounded-lg border border-line bg-surface-elevated text-content focus:outline-none"
          >
            <option value="">{t('common.all', 'Alle')}</option>
            {stationen.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] text-[var(--text-tertiary)] uppercase">Status</label>
          <select
            value={filters.status}
            onChange={(e) => { setFilters(f => ({ ...f, status: e.target.value })); setPage(1); }}
            className="block px-3 py-1.5 text-sm rounded-lg border border-line bg-surface-elevated text-content focus:outline-none"
          >
            <option value="">{t('common.all', 'Alle')}</option>
            <option value="completed">{t('packTimer.completed', 'Abgeschlossen')}</option>
            <option value="running">{t('packTimer.running', 'L\u00e4uft')}</option>
            <option value="timeout">{t('packTimer.timeout', 'Timeout')}</option>
          </select>
        </div>
        <button
          onClick={() => { setFilters({ datum: '', station_id: '', ma_id: '', status: '' }); setPage(1); }}
          className="px-3 py-1.5 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] rounded-lg border border-line hover:bg-surface-hover"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
        <div className="flex-1" />
        <button
          onClick={exportCsv}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#338edc] border border-[#338edc]/30 rounded-lg hover:bg-[#338edc]/5"
        >
          <Download className="w-4 h-4" />
          CSV
        </button>
      </div>

      {/* Table */}
      <div className="bg-surface-elevated rounded-card border border-line overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line bg-surface">
                <th className="px-4 py-2.5 text-left text-[10px] font-medium text-[var(--text-tertiary)] uppercase">{t('packTimer.zeit', 'Zeit')}</th>
                <th className="px-4 py-2.5 text-left text-[10px] font-medium text-[var(--text-tertiary)] uppercase">{t('packTimer.station', 'Station')}</th>
                <th className="px-4 py-2.5 text-left text-[10px] font-medium text-[var(--text-tertiary)] uppercase">{t('packTimer.mitarbeiter', 'Mitarbeiter')}</th>
                <th className="px-4 py-2.5 text-left text-[10px] font-medium text-[var(--text-tertiary)] uppercase">{t('packTimer.kunde', 'Kunde')}</th>
                <th className="px-4 py-2.5 text-right text-[10px] font-medium text-[var(--text-tertiary)] uppercase">{t('packTimer.artikel', 'Artikel')}</th>
                <th className="px-4 py-2.5 text-right text-[10px] font-medium text-[var(--text-tertiary)] uppercase">SOLL</th>
                <th className="px-4 py-2.5 text-right text-[10px] font-medium text-[var(--text-tertiary)] uppercase">IST</th>
                <th className="px-4 py-2.5 text-right text-[10px] font-medium text-[var(--text-tertiary)] uppercase">{t('packTimer.effizienz', 'Effizienz')}</th>
                <th className="px-4 py-2.5 text-center text-[10px] font-medium text-[var(--text-tertiary)] uppercase">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-[var(--text-tertiary)]">{t('common.loading', 'Laden...')}</td></tr>
              ) : sessions.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-[var(--text-tertiary)]">{t('packTimer.keineSessions', 'Keine Sessions gefunden')}</td></tr>
              ) : sessions.map(s => (
                <tr key={s.id} className="border-b border-line last:border-0 hover:bg-surface-hover">
                  <td className="px-4 py-2.5 text-[var(--text-secondary)] whitespace-nowrap">
                    {s.started_at ? new Date(s.started_at).toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' }) : '-'}
                  </td>
                  <td className="px-4 py-2.5 text-[var(--text-primary)]">{s.station_name || '-'}</td>
                  <td className="px-4 py-2.5 text-[var(--text-primary)]">{s.ma_name || '-'}</td>
                  <td className="px-4 py-2.5 text-[var(--text-primary)]">{s.kunde_name || '-'}</td>
                  <td className="px-4 py-2.5 text-right text-[var(--text-primary)] font-mono">{s.artikel_count}</td>
                  <td className="px-4 py-2.5 text-right text-[var(--text-secondary)] font-mono">{s.soll_minuten?.toFixed(1)}</td>
                  <td className="px-4 py-2.5 text-right text-[var(--text-primary)] font-mono font-medium">{s.ist_minuten?.toFixed(1) || '-'}</td>
                  <td className="px-4 py-2.5 text-right">
                    <EffizienzBadge pct={s.effizienz_pct} />
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                      s.status === 'completed' ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                      s.status === 'running' ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' :
                      'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                    }`}>{s.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-line">
            <span className="text-xs text-[var(--text-tertiary)]">
              {total} {t('packTimer.eintraege', 'Eintr\u00e4ge')} | {t('packTimer.seite', 'Seite')} {page}/{totalPages}
            </span>
            <div className="flex items-center gap-1">
              <button
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
                className="px-2.5 py-1 text-xs rounded border border-line hover:bg-surface-hover disabled:opacity-40"
              >{t('common.previous', 'Zur\u00fcck')}</button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
                className="px-2.5 py-1 text-xs rounded border border-line hover:bg-surface-hover disabled:opacity-40"
              >{t('common.next', 'Weiter')}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
